import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';
import strUtil from 'util/strUtil';

interface ClinicalStudyOverviewTableProps {
  title: string;
  columnNames: Array<{name: string; width: number}>;
  tableRows: Array<JSX.Element>;
  titleButton?: JSX.Element | null;
  rightHeader?: JSX.Element | null;
  collapsedHeaderText?: string;
}

/**
 * Renders an empty row with a message and button to the user to define samples
 * @param message
 * @param children
 */
export function renderEmptyRow(message: string, children?: JSX.Element): JSX.Element {
  return (
    <RichObjectTable.Row key={1}>
      <RichObjectTable.Cell className={css(styles.noRowsBackground)}>
        <div className={css(styles.noRowsContainer)}>
          <div className={css(styles.noRowsText)}>{message}</div>
          {children}
        </div>
      </RichObjectTable.Cell>
    </RichObjectTable.Row>
  );
}

function getColumnsAndHeaderCells(
  columnNames: Array<{name: string; width: number}>
): Array<Array<JSX.Element>> {
  const columns: Array<JSX.Element> = [];
  const headerCells: Array<JSX.Element> = [];
  columnNames.forEach(({name, width}) => {
    headerCells.push(<div className={css(styles.columnHeaderText)}>{strUtil.toSentenceCase(name)}</div>);
    columns.push(<col key={name.toLowerCase()} width={`${width}%`} />);
  });
  return [columns, headerCells];
}

export default function ClinicalStudyOverviewTable(props: ClinicalStudyOverviewTableProps): JSX.Element {
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const toggleTableCollapsed = (): void => {
    setIsTableCollapsed(!isTableCollapsed);
  };

  const [columns, headerCells] = getColumnsAndHeaderCells(props.columnNames);

  return (
    /* eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing */
    <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: isTableCollapsed}}>
      <RichObjectTable.Table
        className={css(styles.overviewTable)}
        useMinimalMargin={true}
        dataTestAttrs={{component: 'ClinicalStudyOverviewTable', element: 'root'}}
        showCollapsedMessage={false}
        isCollapsible={true}
        isCollapsed={isTableCollapsed}
        onToggleCollapsed={toggleTableCollapsed}
        columns={columns}
        headerCells={headerCells}
        headerColor={Colors.WHITE}
        largerHeader={true}
        leftHeader={
          <div>
            <div className={css(styles.headerText)}>{props.title}</div>
            {props.titleButton}
          </div>
        }
        rightHeader={props.rightHeader}
        collapsedHeaderText={props.collapsedHeaderText}
      >
        {props.tableRows}
      </RichObjectTable.Table>
    </TableCollapserContext.Provider>
  );
}

/**
 * Extracts the pinned procedure flowchart version ID from a pinned procedure flowchart version object.
 */
export function getPinnedProcedureFlowchartVersionId(
  pinnedProcedureFlowchartVersion: null | {id?: string; __typename?: string}
): string | null {
  return pinnedProcedureFlowchartVersion != null &&
    !isInaccessible(pinnedProcedureFlowchartVersion) &&
    pinnedProcedureFlowchartVersion.id != null
    ? pinnedProcedureFlowchartVersion.id
    : null;
}

const styles = StyleSheet.create({
  tableHeader: {
    margin: '8px',
    width: '100%',
  },
  columnHeaderText: {
    minHeight: 30,
    paddingTop: 8,
    paddingLeft: 12,
    marginTop: -5,
    marginBottom: -5,
    marginLeft: -8,
    marginRight: -8,
    backgroundColor: Colors.GRAY_LIGHTER,
  },
  headerText: {
    fontSize: 13,
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: 16,
    paddingLeft: 4,
  },
  overviewTable: {
    marginRight: 30,
    marginTop: 0,
  },
  noRowsContainer: {
    textAlign: 'center',
  },
  noRowsText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginBottom: 7,
  },
  noRowsBackground: {
    backgroundColor: Color.GRAY_50,
    height: 100,
  },
});
